import React from "react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { DURATION_UNITS } from "../../../../utils/durations";
import { CONTENT_ICON_BY_NAME } from "../../../content/contentTypeIcons";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  activityStatusOptions,
  dateTimeColumn,
  durationColumn,
  statusColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../../dataTable/dataTableStyles";
import { contentPath } from "../../../routes/routes";
import IconByPlatform, {
  getPlatformFilterOptions,
  hasPlatformIcon,
} from "../../../utils/IconByPlatform";

import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";

import type { ViewedContentRow } from "./viewsColumns.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface ViewsColumnsOptions {
  t: TFunction;
  contentAbility: boolean;
}

const CONTENT_TYPE_ICON_BY_NAME: Record<string, LucideIcon | undefined> =
  CONTENT_ICON_BY_NAME;

export function getViewedContentHref(
  row: ViewedContentRow,
  contentAbility: boolean,
): string | undefined {
  return contentAbility
    ? contentPath(row.media_type, row.media_uuid)
    : undefined;
}

export function viewsColumns({
  t,
  contentAbility,
}: ViewsColumnsOptions): DataTableColumnDef<ViewedContentRow>[] {
  return [
    statusColumn<ViewedContentRow>(
      "content_status",
      t("content_status"),
      activityStatusOptions(t),
    ),
    {
      accessorKey: "content_type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        label: t("type"),
        width: "8%",
        minWidth: 50,
        align: "center",
        filterOptions: Object.entries(CONTENT_ICON_BY_NAME).map(
          ([value, TypeIcon]) => ({
            value,
            label: t(`editor-content:tooltip_${value}`),
            icon: <TypeIcon />,
          }),
        ),
      },
      cell: ({ row }) => {
        const TypeIcon = CONTENT_TYPE_ICON_BY_NAME[row.original.content_type];
        return TypeIcon ? (
          <span
            className="inline-flex align-middle"
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={t(
              `editor-content:tooltip_${row.original.content_type}`,
            )}
          >
            <TypeIcon className={CELL_ICON_CLASS} />
          </span>
        ) : null;
      },
    },
    {
      accessorKey: "media_label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("content_label")} />
      ),
      meta: { label: t("content_label"), searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getViewedContentHref(row.original, contentAbility)}
        >
          {row.original.media_label}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "platform",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("platform")} />
      ),
      enableSorting: false,
      meta: {
        label: t("platform"),
        width: "12%",
        minWidth: 105,
        align: "center",
        filterOptions: getPlatformFilterOptions((platform) =>
          t(`editor-platform:${platform}`),
        ),
      },
      cell: ({ row }) => {
        if (!hasPlatformIcon(row.original.platform)) return null;

        const label = t(`editor-platform:${row.original.platform}`);
        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={label}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <IconByPlatform platform={row.original.platform} />
            <span className="sr-only">{label}</span>
          </span>
        );
      },
    },
    durationColumn<ViewedContentRow>(
      "media_duration",
      t("media_duration_label"),
      DURATION_UNITS.MILLISECONDS,
      "fastDuration",
      { width: "14%", minWidth: 175, align: "center" },
    ),
    durationColumn<ViewedContentRow>(
      "playtime",
      t("playtime_label"),
      DURATION_UNITS.SECONDS,
      "fastDuration",
      { width: "12%", minWidth: 125, align: "center" },
    ),
    {
      accessorKey: "play_percentage",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("play_percentage_label")}
        />
      ),
      meta: {
        label: t("play_percentage_label"),
        width: "11%",
        minWidth: 135,
        align: "center",
      },
      cell: ({ row }) =>
        row.original.play_percentage
          ? `${Math.round(row.original.play_percentage * 100) / 100}%`
          : "",
    },
    dateTimeColumn<ViewedContentRow>("datetime", t("view_date_short"), {
      width: "16%",
      minWidth: 160,
    }),
  ];
}
